import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { MultiMonthCalendarControls } from "@src/components/ui-kit";
import type { DateRange as Range } from "@src/utils/types";
import {
  Calendar,
  CalendarDays,
  CalendarMonth,
  CalendarMonthName,
  CalendarWeek,
} from "@uselessdev/datepicker";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import { useStore } from "../hooks";

export interface DatePickerModalProps {
  isOpen: ModalProps["isOpen"];
  onClose: ModalProps["onClose"];
  title: string;
  selectedDate: Range;
  setSelectedDate: (date: Range) => void;
  primaryAction: {
    title: string;
    action: () => void;
  };
  loading?: boolean;
}

export const DatePickerModal: FC<DatePickerModalProps> = observer(
  function DatePickerModal({
    isOpen,
    onClose,
    loading,
    title,
    primaryAction,
    setSelectedDate,
    selectedDate,
  }) {
    const { workspaceStore } = useStore();
    const onCloseWrapper = () => {
      if (!loading) {
        onClose();
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={onCloseWrapper} trapFocus={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            sx={{
              ">div": {
                "box-shadow": "none",
                border: "none",
              },
            }}
            justifyContent="center"
            display="flex"
            p="0"
          >
            <Calendar
              singleDateSelection
              weekStartsOn={workspaceStore.settings?.startOfWeekNumber}
              highlightToday
              allowSelectSameDay
              allowOutsideDays
              disableFutureDates
              value={selectedDate}
              onSelectDate={(date) => {
                if (typeof date === "number") return;
                if (date instanceof Date) {
                  setSelectedDate({ start: date, end: date });
                  return;
                }
                if (
                  date.start &&
                  date.end &&
                  date.start instanceof Date &&
                  date.end instanceof Date
                ) {
                  setSelectedDate({ start: date.start, end: date.end });
                }
              }}
            >
              <VStack pos="relative" p="4" spacing="4">
                <MultiMonthCalendarControls />
                <CalendarMonth>
                  <CalendarMonthName />
                  <CalendarWeek />
                  <CalendarDays />
                </CalendarMonth>
              </VStack>
            </Calendar>
          </ModalBody>
          <ModalFooter gap="4">
            <Button
              colorScheme="grey"
              isDisabled={loading}
              isLoading={loading}
              onClick={onClose}
              variant="outline"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              isDisabled={loading}
              isLoading={loading}
              onClick={primaryAction.action}
            >
              {primaryAction.title}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);
